/**
 * Project Financials workspace: plan, record, review, approve, analyse - for one project.
 *
 * Four views over a single financial dataset. Overview asks "are we on budget?", Budget holds
 * the approved plan, Journal is the transaction ledger, Reports aggregates and exports. Nothing
 * here keeps its own copy of "actuals": every actual is the sum of approved journal entries, so
 * the four views can never disagree.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Plus, ArrowRight, Download, UploadSimple, ChartLineUp, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { api } from '../../../context/AuthContext';
import { cn } from '../../../lib/utils';
import { FinanceEntryFormDialog } from './FinanceEntryFormDialog';
import { FinanceKpiBar } from './FinanceKpiBar';
import { FinancialsSummaryStrip } from './FinancialsSummaryStrip';
import { BudgetVsActualTable, buildRows } from './BudgetVsActualTable';
import { FinanceJournal } from './FinanceJournal';
import { ImportWizardDialog } from './ImportWizardDialog';
import { FinanceReportsPanel } from './FinanceReportsPanel';
import { ProjectCashChart } from './ProjectCashChart';
import {
  PhaseBudgetChart, CategoryBudgetChart, MonthlySpendChart, CumulativeSpendChart,
} from './FinanceCharts';
import { money, percent, monthLabel, toCsv, downloadCsv, varianceOf, varianceClass } from './financeFormat';

const VIEWS = [
  { key: 'overview', label: 'Overview' },
  { key: 'budget', label: 'Budget' },
  { key: 'journal', label: 'Journal' },
  { key: 'reports', label: 'Reports' },
];

const GROUPINGS = [
  { key: 'phase', label: 'Phase' },
  { key: 'category', label: 'Category' },
  { key: 'month', label: 'Month' },
];

const SectionCard = ({ title, description, action, children, testId }) => (
  <section className="bg-white rounded-xl border border-black/[0.04]" data-testid={testId}>
    <header className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-black/[0.05]">
      <div>
        <h3 className="text-sm font-semibold">{title}</h3>
        {description && <p className="text-xs text-foreground-muted mt-0.5">{description}</p>}
      </div>
      {action}
    </header>
    {children}
  </section>
);

const SectionHeader = ({ title, description, action, testId }) => (
  <div className="flex flex-wrap items-end justify-between gap-3" data-testid={testId}>
    <div>
      <h2 className="text-[11px] font-semibold uppercase tracking-[0.16em] text-foreground-muted">{title}</h2>
      {description && <p className="text-[13px] text-foreground-muted mt-1">{description}</p>}
    </div>
    {action}
  </div>
);

export const ProjectFinancialsWorkspace = ({ projectId, projectType, currency = 'EUR' }) => {
  const [view, setView] = useState('overview');
  const [groupBy, setGroupBy] = useState('phase');
  const [report, setReport] = useState(null);
  const [months, setMonths] = useState([]);
  const [pending, setPending] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEntryDialog, setShowEntryDialog] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [batches, setBatches] = useState([]);
  const [journalFilters, setJournalFilters] = useState(null);
  const [history, setHistory] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);
  // A chart click narrows the financial view below it, so pattern and detail stay connected
  const [focus, setFocus] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [reportRes, monthsRes, pendingRes, batchesRes, historyRes] = await Promise.all([
        api.get(`/admin/v2/finance/projects/${projectId}/budget-vs-actual`, {
          params: { project_type: projectType || undefined },
        }),
        api.get(`/admin/v2/finance/projects/${projectId}/monthly`),
        api.get(`/admin/v2/finance/projects/${projectId}/pending-summary`),
        api.get(`/admin/v2/finance/projects/${projectId}/import/batches`),
        api.get(`/admin/v2/finance/projects/${projectId}/budget/history`, {
          params: { project_type: projectType || undefined },
        }),
      ]);
      setReport(reportRes.data);
      setMonths(monthsRes.data.months || []);
      setPending(pendingRes.data);
      setBatches(batchesRes.data.batches || []);
      setHistory(historyRes.data);
    } catch (err) {
      toast.error('Failed to load project financials');
    } finally {
      setLoading(false);
    }
  }, [projectId, projectType]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const rows = useMemo(
    () => buildRows({ groupBy, report, months, projectType }),
    [groupBy, report, months, projectType]
  );

  const budgetRows = useMemo(
    () => buildRows({ groupBy: 'category', report, months, projectType }),
    [report, months, projectType]
  );

  const phaseLabel = useCallback(
    (key) => (report?.by_phase || []).find((p) => p.phase === key)?.phase_label || key,
    [report]
  );

  // A chart selection switches the table to that grouping and shows only the selected line
  const visibleRows = useMemo(() => {
    if (!focus || focus.type !== groupBy) return rows;
    const match = rows.filter((row) => row.key === focus.key);
    return match.length > 0 ? match : rows;
  }, [rows, focus, groupBy]);

  // A true cash flow needs both sides of the ledger; without income it is only spend
  const hasCashFlow = useMemo(
    () => (report?.by_phase || []).some((p) => Number(p.income?.budget || 0) || Number(p.income?.actual || 0)),
    [report]
  );

  const openJournal = (filters) => {
    setJournalFilters(filters);
    setView('journal');
  };

  const exportReport = () => {
    downloadCsv(
      `budget-vs-actual-${groupBy}-${projectId}.csv`,
      toCsv(
        [
          { label: 'Line', value: (r) => r.label },
          { label: 'Cost budget', value: (r) => r.costs?.budget || 0 },
          { label: 'Cost actual', value: (r) => r.costs?.actual || 0 },
          { label: 'Income budget', value: (r) => r.income?.budget || 0 },
          { label: 'Income actual', value: (r) => r.income?.actual || 0 },
          { label: 'Net plan', value: (r) => (r.income?.budget || 0) - (r.costs?.budget || 0) },
          { label: 'Net actual', value: (r) => (r.income?.actual || 0) - (r.costs?.actual || 0) },
        ],
        rows
      )
    );
  };

  const draftCount = Number(pending?.draft_count || 0);

  return (
    <div className="space-y-5" data-testid="project-financials-workspace">
      {/* Header actions + view tabs */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <nav className="flex items-center gap-1 p-1 rounded-lg bg-black/[0.04]" data-testid="financials-view-tabs">
          {VIEWS.map((item) => (
            <button
              key={item.key}
              onClick={() => setView(item.key)}
              className={cn(
                'px-3 py-1.5 text-sm rounded-md transition-colors',
                view === item.key ? 'bg-white shadow-sm font-medium' : 'text-foreground-muted hover:text-foreground'
              )}
              data-testid={`financials-tab-${item.key}`}
            >
              {item.label}
              {item.key === 'journal' && draftCount > 0 && (
                <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800">
                  {draftCount}
                </span>
              )}
              {item.key === 'budget' && Number(history?.plan_version || 1) > 1 && (
                <span
                  className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-black/[0.06] text-foreground-muted"
                  data-testid="budget-plan-version-badge"
                >
                  v{history.plan_version}
                </span>
              )}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={exportReport} data-testid="financials-export">
            <Download className="h-4 w-4 mr-1.5" />
            Export
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowImport(true)} data-testid="financials-import">
            <UploadSimple className="h-4 w-4 mr-1.5" />
            Import
          </Button>
          <Button size="sm" onClick={() => setShowEntryDialog(true)} data-testid="financials-add-entry">
            <Plus className="h-4 w-4 mr-1.5" />
            Add Entry
          </Button>
        </div>
      </div>

      <FinancialsSummaryStrip report={report} pending={pending} currency={currency} />

      {draftCount > 0 && view !== 'journal' && (
        <button
          className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-amber-200 bg-amber-50/70 text-left hover:bg-amber-50 transition-colors"
          onClick={() => openJournal({ status: 'draft,pending_approval' })}
          data-testid="financials-review-drafts"
        >
          <span className="text-sm">
            <strong className="font-semibold">{draftCount} draft transaction{draftCount === 1 ? '' : 's'}</strong>
            {' '}awaiting review · {money(pending?.draft_amount, { currency })} not yet in actuals
          </span>
          <span className="inline-flex items-center gap-1 text-sm font-medium text-amber-900">
            Review drafts <ArrowRight className="h-4 w-4" />
          </span>
        </button>
      )}

      {loading ? (
        <div className="py-16 text-center text-sm text-foreground-muted">Loading financials…</div>
      ) : view === 'overview' ? (
        <div className="space-y-6">
          <FinanceKpiBar kpis={report?.kpis} projectType={projectType} />

          <div className="space-y-5">
            <SectionHeader
              title="Project performance"
              description="Budget and actual project costs across phases, categories and time."
              testId="financials-performance-header"
            />

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 items-stretch">
              <div className="min-w-0 h-full">
                <PhaseBudgetChart
                  report={report}
                  currency={currency}
                  selected={focus?.type === 'phase' ? focus.key : null}
                  onSelect={(key) => {
                    setGroupBy('phase');
                    setFocus((f) => (f?.type === 'phase' && f.key === key ? null : { type: 'phase', key, label: phaseLabel(key) }));
                  }}
                  onAddEntry={() => setShowEntryDialog(true)}
                  onConfigureBudget={() => setView('budget')}
                />
              </div>
              <div className="min-w-0 h-full">
                <CategoryBudgetChart
                  report={report}
                  currency={currency}
                  selected={focus?.type === 'category' ? focus.key : null}
                  onSelect={(key) => {
                    setGroupBy('category');
                    setFocus((f) => (f?.type === 'category' && f.key === key ? null : { type: 'category', key, label: key }));
                  }}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 items-stretch">
              <div className="min-w-0 h-full">
                <MonthlySpendChart months={months} currency={currency} />
              </div>
              <div className="min-w-0 h-full">
                <CumulativeSpendChart months={months} currency={currency} />
              </div>
            </div>

            {hasCashFlow && (
              <section
                className="bg-white rounded-xl border border-black/[0.06] p-5 lg:p-6"
                data-testid="financials-cash-chart"
              >
                <header>
                  <h3 className="text-base font-semibold leading-snug">Project Cash Flow</h3>
                  <p className="text-[13px] text-foreground-muted mt-1">
                    Money in and out each month, with capital raises and distributions marked as
                    events, beside the running cash position.
                  </p>
                </header>
                <ProjectCashChart projectId={projectId} currency={currency} />
              </section>
            )}
          </div>

          <div className="space-y-5 pt-1">
            <SectionHeader
              title="Financial view"
              description="The same records read by phase, category or month."
              testId="financials-view-header"
              action={
                <div className="flex items-center gap-2">
                  {focus && (
                    <button
                      className="inline-flex items-center gap-1.5 pl-2.5 pr-2 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15 transition-colors"
                      onClick={() => setFocus(null)}
                      data-testid="financials-focus-chip"
                    >
                      {focus.label}
                      <span aria-hidden className="text-sm leading-none">×</span>
                    </button>
                  )}
                  <div className="flex items-center gap-1 p-1 rounded-lg bg-black/[0.04]">
                    {GROUPINGS.map((g) => (
                      <button
                        key={g.key}
                        onClick={() => { setGroupBy(g.key); setFocus(null); }}
                        className={cn(
                          'px-2.5 py-1 text-xs rounded-md transition-colors',
                          groupBy === g.key ? 'bg-white shadow-sm font-medium' : 'text-foreground-muted'
                        )}
                        data-testid={`financials-group-${g.key}`}
                      >
                        {g.label}
                      </button>
                    ))}
                  </div>
                </div>
              }
            />

            <section className="bg-white rounded-xl border border-black/[0.06]" data-testid="financials-bva-section">
              <BudgetVsActualTable
                rows={visibleRows}
                currency={currency}
                onDrill={(parent, child) =>
                  openJournal(
                    groupBy === 'phase'
                      ? { phase: parent.key, category: child.category || '' }
                      : { category: parent.key }
                  )
                }
                emptyLabel="No budget or actual entries yet. Add an entry to start tracking this project."
              />
            </section>
          </div>
        </div>
      ) : view === 'budget' ? (
        <div className="space-y-5">
          <SectionCard
            title={`Current budget (approved cost plan) · v${history?.plan_version || 1}`}
            description="The financial plan in force today. Editing any line moves the plan to the next version."
            testId="financials-budget-section"
          >
            <BudgetVsActualTable
              rows={budgetRows}
              currency={currency}
              emptyLabel="No budget lines yet. Add a budget entry to build the plan."
            />
          </SectionCard>
          <p className="text-xs text-foreground-muted px-1">
            Actuals shown here are the sum of approved journal entries - there is no separately
            stored actual figure anywhere in Flippex.
          </p>
        </div>
      ) : view === 'journal' ? (
        <div className="space-y-5">
          {batches.length > 0 && (
            <SectionCard
              title="Import batches"
              description="Every imported transaction traces back to the file it came from"
              testId="financials-import-batches"
            >
              <div className="divide-y divide-black/[0.04]">
                {batches.map((b) => (
                  <div key={b.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5" data-testid={`import-batch-${b.code}`}>
                    <span className="text-sm font-medium">{b.code}</span>
                    <span className="text-sm text-foreground-muted truncate max-w-[220px]">{b.filename}</span>
                    <span className="text-xs text-foreground-muted">
                      {b.entries_created} entries · {money(b.entries_total, { currency })}
                      {b.skipped_duplicates > 0 && ` · ${b.skipped_duplicates} duplicate skipped`}
                    </span>
                    <Badge
                      variant="outline"
                      className={cn('text-[10px]',
                        b.pending_entries > 0 ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200')}
                    >
                      {b.approval_state}
                    </Badge>
                    <span className="text-xs text-foreground-muted ml-auto">
                      {b.imported_at ? new Date(b.imported_at).toLocaleDateString('en-GB') : 'Not imported'}
                    </span>
                    {b.pending_entries > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => setJournalFilters({ status: 'draft,pending_approval' })}
                        data-testid={`import-batch-review-${b.code}`}
                      >
                        Review {b.pending_entries}
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-foreground-muted hover:text-red-600"
                      title="Discard this batch and its unapproved drafts"
                      onClick={async () => {
                        if (!window.confirm(`Discard batch ${b.code} and its ${b.pending_entries} unapproved draft(s)?`)) return;
                        try {
                          const res = await api.delete(`/admin/v2/finance/import/${b.id}`);
                          toast.success(`Batch discarded · ${res.data.drafts_removed} drafts removed`);
                          fetchAll();
                        } catch {
                          toast.error('Could not discard the batch');
                        }
                      }}
                      data-testid={`import-batch-delete-${b.code}`}
                    >
                      <Trash className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            </SectionCard>
          )}
          <FinanceJournal
            projectId={projectId}
            projectType={projectType}
            currency={currency}
            initialFilters={journalFilters}
            onChanged={fetchAll}
            onEdit={(entry) => { setEditingEntry(entry); setShowEntryDialog(true); }}
          />
        </div>
      ) : (
        <div className="space-y-5">
          <FinanceReportsPanel
            projectId={projectId}
            projectType={projectType}
            currency={currency}
            onDrill={(filters) => openJournal(filters)}
          />

          <SectionCard
            title="Budget vs actual by phase"
            description="Export-ready performance summary"
            testId="reports-by-phase"
          >
            <BudgetVsActualTable
              rows={buildRows({ groupBy: 'phase', report, months, projectType })}
              currency={currency}
              expandable={false}
            />
          </SectionCard>

          <SectionCard title="Project profit & loss" description="Derived from approved entries only" testId="reports-pnl">
            <div className="px-4 py-4">
              {(() => {
                const phases = report?.by_phase || [];
                const income = phases.reduce((s, p) => s + Number(p.income?.actual || 0), 0);
                const incomeBudget = phases.reduce((s, p) => s + Number(p.income?.budget || 0), 0);
                const costs = phases.reduce((s, p) => s + Number(p.costs?.actual || 0), 0);
                const costsBudget = phases.reduce((s, p) => s + Number(p.costs?.budget || 0), 0);
                const profitVariance = varianceOf(incomeBudget - costsBudget, income - costs, true);
                const lines = [
                  ['Total revenue', incomeBudget, income, true],
                  ['Total costs', costsBudget, costs, false],
                ];
                return (
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-black/[0.08]">
                        <th className="py-2 text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-foreground-muted">Line</th>
                        <th className="py-2 text-right text-[10px] font-semibold uppercase tracking-[0.12em] text-foreground-muted">Plan</th>
                        <th className="py-2 text-right text-[10px] font-semibold uppercase tracking-[0.12em] text-foreground-muted">Actual</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map(([label, budget, actual]) => (
                        <tr key={label} className="border-b border-black/[0.04]">
                          <td className="py-2.5 text-sm">{label}</td>
                          <td className="py-2.5 text-sm text-right tabular-nums text-foreground-muted">{money(budget, { currency })}</td>
                          <td className="py-2.5 text-sm text-right tabular-nums font-medium">{money(actual, { currency })}</td>
                        </tr>
                      ))}
                      <tr className="bg-black/[0.03]">
                        <td className="py-3 text-sm font-semibold">Project profit</td>
                        <td className="py-3 text-sm text-right font-semibold tabular-nums">{money(incomeBudget - costsBudget, { currency })}</td>
                        <td className={cn('py-3 text-sm text-right font-semibold tabular-nums', varianceClass(profitVariance))}>
                          {money(income - costs, { currency })}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                );
              })()}
            </div>
          </SectionCard>

          <SectionCard title="Monthly ledger summary" description="Budget vs actual by month" testId="reports-monthly">
            <BudgetVsActualTable
              rows={buildRows({ groupBy: 'month', report, months, projectType })}
              currency={currency}
              expandable={false}
              emptyLabel="No dated entries yet."
            />
          </SectionCard>

          <div className="flex items-center gap-2 px-1">
            <ChartLineUp className="h-4 w-4 text-foreground-muted" />
            <p className="text-xs text-foreground-muted">
              {history?.is_baselined
                ? `Actuals are compared against the current cost plan (v${history.plan_version || 1}); the plan as first written is kept for every project.`
                : 'Every export carries the project, the period and the rule that produced the actuals.'}
            </p>
          </div>
        </div>
      )}

      <ImportWizardDialog
        open={showImport}
        onOpenChange={setShowImport}
        projectId={projectId}
        projectType={projectType}
        onImported={(action) => {
          fetchAll();
          if (action === 'review') openJournal({ status: 'draft,pending_approval' });
        }}
      />

      <FinanceEntryFormDialog
        open={showEntryDialog}
        onOpenChange={(open) => { setShowEntryDialog(open); if (!open) setEditingEntry(null); }}
        entry={editingEntry}
        projectId={projectId}
        projectType={projectType}
        onSave={() => { setShowEntryDialog(false); setEditingEntry(null); fetchAll(); }}
      />
    </div>
  );
};

export default ProjectFinancialsWorkspace;
